import React from 'react';
import { useDispatch } from 'react-redux';
import { Link } from 'react-router-dom';
import Immutable from 'immutable';
import PropTypes from 'prop-types';
import moment from 'moment';
import ImmutablePropTypes from 'react-immutable-proptypes';
import { FormattedMessage } from 'react-intl';
import { Star, StarOutline } from '@bibliocommons/base-icons';
import { SecondaryGhostButton, TransactionalSolidButton } from '@bibliocommons/base-buttons';
import Divider from 'app/components/bibs/shared/Divider';
import { Dropdown, DropdownMenuHeader, DropdownMenuItem, DropdownMenuDivider } from '@bibliocommons/base-dropdown';
import ScreenReaderMessage from '@bibliocommons/base-screen-reader-message';
import { libraryShape, userIdentityShape } from '@bibliocommons/bc-prop-types';
import { eventShape } from 'app/shapes/events';
import { UI_CONTAINER_TYPE } from 'app/constants/AnalyticsGa4Constants';
import { MY_EVENTS_BASE_URL, PROVIDERS, REGISTRATION_WINDOW_STATUSES } from 'app/constants/EventsConstants';
import { mapEventToEntity } from 'app/helpers/analytics/formatGA4Entity';
import { getRegistrationStatusMessage, isInProgress } from 'app/helpers/events/utils';
import ModalActions from 'app/actions/ModalActions';
import { trackEventInterested } from 'app/actions/AnalyticsGa4Actions';
import EventsActions from 'app/actions/EventsActions';
import ModalContainer from 'app/components/shared/Modal/ModalContainer';
import AddToCalendar from 'app/components/events/AddToCalendar';
import RemoveRegistrationModal from 'app/components/events/RemoveRegistrationModal';
import EventRegistrationModal from '../EventRegistrationModal';

import './EventActionsPanel.scss';

const ALL_REMAINING = 'ALL_REMAINING';
const CLOSED = 'closed';
const OPEN = 'open';
const FULL = 'full';
const WAITLIST = 'waitlist';
const INACTIVE = 'INACTIVE';

function getEventRegistrationStatus(registrationInfo, registrationWindow, registrationMessage) {
  if (registrationMessage?.status === INACTIVE) {
    return CLOSED;
  }
  if (registrationWindow?.get('status') === REGISTRATION_WINDOW_STATUSES.UPCOMING && registrationWindow.get('windowStart')) {
    return CLOSED;
  }
  if (registrationInfo.get('provider') === PROVIDERS.BIBLIO_EVENTS && registrationMessage?.status === OPEN) {
    return OPEN;
  }
  if (registrationInfo.get('provider') === PROVIDERS.BIBLIO_EVENTS 
    && (registrationMessage?.status === WAITLIST || registrationMessage?.status === FULL)
    && registrationInfo.get('waitlistEnabled')
  ) {
    return WAITLIST;
  }
  return null;
}

export default function EventActionsPanel({
  currentLibrary,
  currentUser, 
  event, 
  eventSeries, 
  isPast, 
  registration, 
  eventSettings, 
  seriesEvents,
  registrationModalKey,
  trackAddToCalendarClick
}) {
  const dispatch = useDispatch();
  const isCancelled = event.getIn(['definition', 'isCancelled']);
  const registrationInfo = event.getIn(['definition', 'registrationInfo']);
  const isSeriesRegistration = registrationInfo?.get('scope') === ALL_REMAINING;
  const registrationWindow = event?.get('registrationWindow');
  const registrationWindowStatus = event?.getIn(['registrationWindow', 'status']);
  const registrationMessage = getRegistrationStatusMessage(event);
  const registered = registration?.get('registered');
  const waitlist = registration?.get('waitlist');
  const isInterested = registration?.get('interested');
  const cancelModalKey = waitlist
    ? `cancel-waitlist-${registration?.get('registrationId')}` 
    : `cancel-registration-${registration?.get('registrationId')}`;
  const status = getEventRegistrationStatus(registrationInfo, registrationWindow, registrationMessage);

  function openEventRegistrationModal(authRequired) {
    dispatch(ModalActions.openModal(registrationModalKey, { authRequired }));
  }

  function closeEventRegistrationModal() {
    dispatch(ModalActions.closeModal(registrationModalKey));
  }

  function openCancelRegistrationModal() {
    dispatch(ModalActions.openModal(cancelModalKey, { authRequired: true }));
  }

  function closeCancelRegistrationModal() {
    dispatch(ModalActions.closeModal(cancelModalKey));
  }

  function addToInterestedEvents() {
    dispatch(
      EventsActions.addToInterestedEvents({
        eventId: event.get('id'),
        seriesId: event.get('seriesId')
      })
    );

    const eventEntity = mapEventToEntity({ event, series: eventSeries });
    const ui = {
      ui_container_type: UI_CONTAINER_TYPE.ITEM,
      ui_content_layout: 'medium'
    };
    
    dispatch(trackEventInterested({ event: eventEntity, ui }));
  }

  function removeFromInterestedEvents() {
    dispatch(
      EventsActions.removeFromInterestedEvents({
        eventId: event.get('id'),
        seriesId: event.get('seriesId'),
        registrationId: registration?.get('registrationId')
      })
    );
  }

  function showRegistration() {
    if (event.getIn(['definition', 'isCancelled'])) {
      return false;
    }
    
    if (!registrationInfo?.get('provider')) {
      return false;
    }

    if (isInProgress(event)) {
      return false;
    }

    if (status === OPEN || status === WAITLIST) {
      return true;
    }

    return false;
  }

  function renderRegisterButtonLabel() {
    if (status === WAITLIST) {
      if (isSeriesRegistration) {
        return <FormattedMessage id="join_waitlist_for_series" defaultMessage="Join waitlist for series" />;
      }
      return <FormattedMessage id="join_waitlist" defaultMessage="Join waitlist" />;
    }
    
    if (isSeriesRegistration) {
      return <FormattedMessage id="register_for_series" defaultMessage="Register for series" />;
    }
    return <FormattedMessage id="register" defaultMessage="Register" />;
  }

  function renderRegisterButton() {
    const eventCount = registration?.get('eventIds')?.size;
    const numberAttending = registration?.get('numberAttending');

    if (currentUser?.get('loggedIn')) {
      if (registered) {
        return (
          <Dropdown
            right
            className="registered-dropdown-button"
            dataKey="registered-event-dropdown"
            buttonTag={TransactionalSolidButton}
            renderLabel={() => <FormattedMessage id="attending" defaultMessage="Attending" />}
          >
            <DropdownMenuHeader>
              {eventCount === 1 && (
                <FormattedMessage id="single_event_registration" values={{ count: numberAttending }} />
              )}
              {eventCount > 1 && (<FormattedMessage id="series_event_registration" values={{ eventCount, count: numberAttending }} />)}
            </DropdownMenuHeader>
            <DropdownMenuItem
              item={Immutable.Map({ label: <FormattedMessage id="cancel_registration" /> })}
              dataKey="cancel-registration-menu"
              handleClick={openCancelRegistrationModal}
            />
            <DropdownMenuDivider />
            <DropdownMenuItem dataKey={`myevents-registered-${event.get('id')}`}>
              {/* eslint-disable-next-line */}
              <Link to={`${MY_EVENTS_BASE_URL}/upcoming/registered`}>
                <FormattedMessage id="go_to_my_events" defaultMessage="Go to My Events" />
              </Link>
            </DropdownMenuItem>
          </Dropdown>
        );
      }

      if (waitlist) {
        return (
          <Dropdown
            right
            className="waitlist-dropdown-button"
            dataKey="waitlist-event-dropdown"
            buttonTag={TransactionalSolidButton}
            renderLabel={() => <FormattedMessage id="waitlist" defaultMessage="On waitlist" />}
          >
            <DropdownMenuHeader>
              {eventCount === 1 && (
                <FormattedMessage id="single_event_registration" values={{ count: numberAttending }} />
              )}
              {eventCount > 1 && (<FormattedMessage id="series_event_registration" values={{ eventCount, count: numberAttending }} />)}
            </DropdownMenuHeader>
            <DropdownMenuItem
              item={Immutable.Map({ label: <FormattedMessage id="cancel_registration" defaultMessage="Cancel registration" /> })}
              dataKey="cancel-waitlist-menu"
              handleClick={openCancelRegistrationModal}
            />
            <DropdownMenuDivider />
            <DropdownMenuItem dataKey={`myevents-waitlist-${event.get('id')}`}>
              {/* eslint-disable-next-line */}
              <Link to={`${MY_EVENTS_BASE_URL}/upcoming/waitlist`}>
                <FormattedMessage id="go_to_my_events" defaultMessage="Go to My Events" />
              </Link>
            </DropdownMenuItem>
          </Dropdown>
        );
      }

      if (!showRegistration()) {
        return null;
      }

      if (status === OPEN) {
        return (
          <TransactionalSolidButton
            className="register-action-button"
            dataKey="register-events-button"
            aria-label="Register"
            handleClick={() => openEventRegistrationModal(false)}
          >
            {renderRegisterButtonLabel()}
          </TransactionalSolidButton>
        );
      }

      if (status === WAITLIST) {
        return (
          <TransactionalSolidButton
            className="register-action-button"
            dataKey="register-events-button"
            aria-label="Join waitlist"
            handleClick={() => openEventRegistrationModal(false)}
          >
            <FormattedMessage id="join_waitlist" defaultMessage="Join waitlist" />
          </TransactionalSolidButton>
        );
      }
    }

    if (!showRegistration()) {
      return null;
    }

    return (
      <Dropdown
        right
        className="register-action-button"
        dataKey="register-event-dropdown"
        buttonTag={TransactionalSolidButton}
        renderLabel={renderRegisterButtonLabel}
      >
        <DropdownMenuItem
          item={Immutable.Map({
            label: <FormattedMessage id="button_login" defaultMessage="Log in" />,
            value: 'log-in'
          })}
          handleClick={() => openEventRegistrationModal(true)}
        />
        {!event.getIn(['definition', 'registrationInfo', 'loginToRegister']) && (
          <DropdownMenuItem
            item={Immutable.Map({
              label: <FormattedMessage id="continue_as_guest" defaultMessage="Continue as guest" />,
              value: 'guest'
            })}
            handleClick={() => openEventRegistrationModal(false)}
          />
        )}
      </Dropdown>
    );
  }

  function renderInterestedButton() {
    if (!currentLibrary.getIn(['events', 'myEventsEnabled'])) return null;
    if (registered || waitlist) return null;

    if (currentUser?.get('loggedIn') && isInterested) {
      return (
        <Dropdown
          right
          className="interested-dropdown-button"
          dataKey="interested-event-dropdown"
          buttonTag={SecondaryGhostButton}
          renderLabel={() => (
            <>
              <Star />
              <FormattedMessage id="interested" defaultMessage="Interested" />
            </>
          )}
        >
          <DropdownMenuItem
            item={Immutable.Map({
              label: <FormattedMessage id="remove_from_my_events" />,
              value: 'remove-from-my-events'
            })}
            handleClick={removeFromInterestedEvents}
          />
          <DropdownMenuDivider />
          <DropdownMenuItem dataKey={`myevents-interested-${event.get('id')}`}>
            {/* eslint-disable-next-line */}
            <Link to={`${MY_EVENTS_BASE_URL}/upcoming/interested`}>
              < FormattedMessage id="go_to_my_events" defaultMessage="Go to My Events" />
            </Link>
          </DropdownMenuItem>
        </Dropdown>
      );
    }

    return (
      <SecondaryGhostButton
        className="add-to-interested-button"
        dataKey="add-to-interested-events-button"
        handleClick={addToInterestedEvents}
        aria-label="Interested"
      >
        <StarOutline />
        <FormattedMessage id="interested" defaultMessage="Add to Interested" />
      </SecondaryGhostButton>
    );
  }

  function renderRegistrationWindow() {
    if (currentUser?.get('loggedIn') && (registered || waitlist)) return null;
    if (event.get('isFull') || event.get('registrationClosed')) return  null;
    
    let message;
    const windowStart = moment(registrationWindow?.get('windowStart'));
    const windowEnd = moment(registrationWindow?.get('windowEnd'));
    const windowStartDate = windowStart?.format('MMMM D');
    const windowStartTime = windowStart?.format('h:mma');
    const windowEndDate = windowEnd?.format('MMMM D');
    const windowEndTime = windowEnd?.format('h:mma');
    
    if (registrationWindowStatus === REGISTRATION_WINDOW_STATUSES.UPCOMING && registrationWindow.get('windowStart')) {
      message = <FormattedMessage id="registration_window_opens_at" values={{ date: windowStartDate, time: windowStartTime }} />;
    }

    if (registrationWindowStatus === REGISTRATION_WINDOW_STATUSES.ACTIVE) {
      if (registrationWindow.get('windowEnd')) {
        message = status === WAITLIST
          ? <FormattedMessage id="waitlist_window_closes_at" values={{ date: windowEndDate, time: windowEndTime }} />
          : <FormattedMessage id="registration_window_closes_at" values={{ date: windowEndDate, time: windowEndTime }} />;
      }
    }

    return message ? (
      <div className="registration-message-info">{message}</div>
    ) : null;
  }

  function renderRegistrationPanelDetails() {
    if (event?.get('isFull') && !registered && !waitlist && !registrationInfo?.get('waitlistEnabled')) {
      return (
        <div className="registration-panel-details">
          <div className="registration-full-message">
            <span><FormattedMessage defaultMessage="All places have been registered." id="events.registration.message.full" /></span>
            <span><FormattedMessage defaultMessage="You can no longer register for this event." id="events.registration.message.not_allowed" /></span>
          </div>
        </div>
      );
    }
    
    if (registrationMessage?.status === CLOSED) {
      return (
        <div className="registration-panel-details">
          <div className="registration-closed-message">
            <span><FormattedMessage defaultMessage={`Registration closed on ${registrationMessage?.values?.date}.`} id="events.registration.message.closed" values={{ date: registrationMessage?.values?.date }} /></span>
            <span><FormattedMessage defaultMessage="You can no longer register for this event." id="events.registration.message.not_allowed" /></span>
          </div>
        </div>
      );      
    }

    return (
      <div className="registration-panel-details">
        {!registered && !waitlist && renderRegistrationWindow()}
        {registrationInfo.get('provider') === PROVIDERS.BIBLIO_EVENTS && renderRegisterButton()}
        {registrationInfo.get('provider') === PROVIDERS.EXTERNAL && (
          // eslint-disable-next-line react/no-danger
          <div className="registration-instructions" dangerouslySetInnerHTML={{ __html: registrationInfo.get('instructions') }} />
        )}
        {!registered && !waitlist && renderInterestedButton()}
        <Dropdown
          right
          className="add-to-calendar-dropdown-button"
          dataKey="add-to-calendar-button"
          buttonTag={SecondaryGhostButton}
          renderLabel={() => <FormattedMessage id="add_to_calendar" />}
        >
          <AddToCalendar event={event} trackAddToCalendarClick={trackAddToCalendarClick} />
        </Dropdown>
      </div>
    );
  }

  function renderRegistrationPanelHeading() {
    if (currentUser?.get('loggedIn') && (registered || waitlist)) return null;
    if (!registrationMessage) {
      return null;
    }

    const isRecurring = event.get('isRecurring');
    
    const upcomingSessions = seriesEvents?.valueSeq()?.filter(e => !e?.getIn(['definition', 'isCancelled'])).toArray();

    const numSeats = registrationInfo?.get('cap') - event.get('numberRegistered');
    let seatsRemaining = <FormattedMessage id="events.registration.seats.remaining" values={{ numSeats }} defaultMessage="seats remaining" />;
    if (registrationMessage?.status === INACTIVE) {
      seatsRemaining = <FormattedMessage id="events.registration.seats" values={{ numSeats }} defaultMessage="seats for this event" />;
    }

    return (
      <div className="registration-panel-heading">
        <div className="heading-section">
          <span className="heading-text">
            <span className={`registration-${registrationMessage?.status}`}>
              {registrationWindowStatus === REGISTRATION_WINDOW_STATUSES.UPCOMING && registrationWindow.get('windowStart') ? (
                <FormattedMessage
                  id="registration_window_opens_in_days"
                  values={{ numberOfDays: registrationMessage?.values?.numberOfDays }}
                  defaultMessage={registrationMessage?.defaultMessage}
                />  
              ) : (
                <FormattedMessage id={`registration_${registrationMessage?.status}`} />
              )}
            </span>
          </span>
          {isSeriesRegistration && isRecurring && upcomingSessions?.length > 0 && (
            <div className="event-info">
              <span className="number-bold">{upcomingSessions?.length}</span>&nbsp;
              <FormattedMessage
                id="events.registration.upcoming.sessions"
                values={{ upcomingSessions }}
                defaultMessage="events in this series"
              />
            </div>
          )}
          {registrationInfo.get('provider') === PROVIDERS.BIBLIO_EVENTS && registrationMessage.status !== FULL && registrationMessage?.status !== CLOSED && (
            <div className="event-info">
              <span className="number-bold">{numSeats}</span>&nbsp;
              {seatsRemaining}
            </div>
          )}
        </div>
        <Divider />
      </div>
    );
  }

  if (isPast || isCancelled) {
    return null;
  }

  return (
    <div className="cp-registration-actions-panel">
      <ScreenReaderMessage>
        <h2><FormattedMessage id="event_actions" defaultMessage="Event actions" /></h2>
      </ScreenReaderMessage>
      {renderRegistrationPanelHeading()}
      {renderRegistrationPanelDetails()}
      <ModalContainer modalKey={registrationModalKey} handleClose={closeEventRegistrationModal}>
        <EventRegistrationModal
          currentLibrary={currentLibrary}
          currentUser={currentUser}
          event={event}
          eventSeries={eventSeries}
          modalKey={registrationModalKey}
          status={status}
          eventSettings={eventSettings}
        />
      </ModalContainer>
      {(registered || waitlist) && (
        <ModalContainer modalKey={cancelModalKey} handleClose={closeCancelRegistrationModal}>
          <RemoveRegistrationModal
            event={event}
            registrationId={registration?.get('registrationId')}
            registeredEvents={registration?.get('eventIds')}
            removeFromWaitlist={false}
            removeFromMyEvents={false}
            removeFromInterested={false}
            handleClose={closeCancelRegistrationModal}
          />
        </ModalContainer>
      )}
    </div>
  );
}

EventActionsPanel.propTypes = {
  currentLibrary: libraryShape.isRequired,
  currentUser: userIdentityShape,
  event: eventShape.isRequired,
  eventSeries: ImmutablePropTypes.map.isRequired,
  isPast: PropTypes.bool.isRequired,
  registration: ImmutablePropTypes.mapContains({
    registrationId: PropTypes.string,
    eventIds: ImmutablePropTypes.list,
    interested: PropTypes.bool,
    registered: PropTypes.bool,
    waitlist: PropTypes.bool
  }),
  eventSettings: ImmutablePropTypes.mapContains({
    googleMapsOptions: ImmutablePropTypes.mapContains({
      apiKey: PropTypes.string.isRequired
    }),
    eventRegistrationOptions: ImmutablePropTypes.mapContains({
      providerExternal: PropTypes.bool.isRequired,
      providerBiblioEvents: PropTypes.bool.isRequired,
      maxSeats: PropTypes.number,
      emailRequiredForWaitlist: PropTypes.bool.isRequired
    })
  }),
  seriesEvents: ImmutablePropTypes.mapOf(eventShape, PropTypes.string),
  registrationModalKey: PropTypes.string.isRequired,
  trackAddToCalendarClick: PropTypes.func.isRequired
};
